import type { ComponentType } from 'react'
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router'

import { GuestRoute } from '@/app/router/guards/GuestRoute'
import { ProtectedRoute } from '@/app/router/guards/ProtectedRoute'
import { paths } from '@/app/router/paths'
import { RouteErrorBoundary } from '@/app/router/RouteErrorBoundary'
import { FullPageLoader } from '@/components/common/FullPageLoader'
import { AppLayout } from '@/components/layout/AppLayout'
import { AuthLayout } from '@/components/layout/AuthLayout'

/** Turns a default-exported page module into a lazily loaded route. */
const page = (load: () => Promise<{ default: ComponentType }>) => async () => ({
  Component: (await load()).default,
})

export const routes: RouteObject[] = [
  {
    errorElement: <RouteErrorBoundary />,
    // Shown while the first lazy route module is still downloading.
    HydrateFallback: FullPageLoader,
    children: [
      {
        element: <GuestRoute />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              {
                path: paths.auth.login,
                lazy: page(() => import('@/features/auth/pages/LoginPage')),
              },
            ],
          },
        ],
      },
      {
        element: <ProtectedRoute />,
        children: [
          {
            element: <AppLayout />,
            children: [
              { index: true, element: <Navigate to={paths.projects.pipeline} replace /> },
              {
                path: paths.projects.pipeline,
                lazy: page(() => import('@/features/projects/pages/PipelinePage')),
              },
              {
                path: paths.projects.new,
                lazy: page(() => import('@/features/projects/pages/NewProjectPage')),
              },
              {
                // Kept below `/requests/new` so the literal wins over the param.
                path: paths.projects.detail(':id'),
                lazy: page(() => import('@/features/projects/pages/ProjectDetailPage')),
              },
              {
                path: paths.people.roster,
                lazy: page(() => import('@/features/people/pages/PeoplePage')),
              },
              {
                path: paths.people.detail(':id'),
                lazy: page(() => import('@/features/people/pages/PersonDetailPage')),
              },
              {
                // A second role gate nested inside the first: signed in is not enough here.
                element: <ProtectedRoute roles={['Administrator']} />,
                children: [
                  {
                    path: paths.admin.users,
                    lazy: page(() => import('@/features/users/pages/UsersPage')),
                  },
                ],
              },
              {
                path: paths.notFound,
                lazy: page(() => import('@/features/misc/pages/NotFoundPage')),
              },
            ],
          },
        ],
      },
    ],
  },
]

export const router = createBrowserRouter(routes)
